/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, useCallback, useState, useMemo, useRef } from "react";

/** Minimal device shape for action renderers -- matches DeviceCard's Device interface */
interface SearchDevice {
  id: string;
  name: string;
  devEUI: string;
  deviceType?: {
    id: string;
    name: string;
    description?: string;
    category?: string;
  };
  status?: string;
  lastSeenAt?: string;
  externalTags?: Record<string, any> | string[] | null;
  externalConfig?: Record<string, any>;
  serialNumber?: string;
  model?: string;
  manufacturer?: string;
}

export type DeviceActionRenderer = (device: SearchDevice) => React.ReactNode;

interface UniversalSearchContextValue {
  isEnabled: boolean;
  setEnabled: (enabled: boolean) => void;
  customHandler: ((barcode: string) => void) | null;
  setCustomHandler: (handler: ((barcode: string) => void) | null) => void;
  /**
   * Acquire a "disable hold" - increments the disable counter.
   * Universal search is disabled when any holds are active.
   * Returns a release function that should be called when the component unmounts or no longer needs to disable.
   */
  acquireDisableHold: () => () => void;
  /** Optional renderer for custom device action buttons in search results */
  deviceActionRenderer: DeviceActionRenderer | null;
  setDeviceActionRenderer: (renderer: DeviceActionRenderer | null) => void;
  /** Close the search popup (set by UniversalSearch when mounted) */
  closeSearch: (() => void) | null;
  setCloseSearch: (fn: (() => void) | null) => void;
}

const UniversalSearchContext = createContext<UniversalSearchContextValue | null>(null);

interface UniversalSearchProviderProps {
  children: React.ReactNode;
}

export function UniversalSearchProvider({ children }: UniversalSearchProviderProps) {
  const [isEnabled, setIsEnabled] = useState(true);
  // Use state instead of ref so context value updates when handler changes
  const [customHandler, setCustomHandlerState] = useState<((barcode: string) => void) | null>(null);
  const [deviceActionRenderer, setDeviceActionRendererState] =
    useState<DeviceActionRenderer | null>(null);
  const [closeSearch, setCloseSearchState] = useState<(() => void) | null>(null);

  // Ref-counting for disable holds - allows multiple components to disable without conflicts
  const disableHoldCount = useRef(0);

  const setEnabled = useCallback((enabled: boolean) => {
    setIsEnabled(enabled);
  }, []);

  // Use function form of setState to properly store function values
  const setCustomHandler = useCallback((handler: ((barcode: string) => void) | null) => {
    setCustomHandlerState(() => handler);
  }, []);

  const setDeviceActionRenderer = useCallback((renderer: DeviceActionRenderer | null) => {
    setDeviceActionRendererState(() => renderer);
  }, []);

  const setCloseSearch = useCallback((fn: (() => void) | null) => {
    setCloseSearchState(() => fn);
  }, []);

  /**
   * Acquire a disable hold. While any holds are active, universal search is disabled.
   * Returns a release function that must be called to release the hold.
   * This prevents race conditions when multiple components need to disable universal search.
   */
  const acquireDisableHold = useCallback(() => {
    disableHoldCount.current += 1;
    if (disableHoldCount.current === 1) {
      // First hold acquired - disable universal search
      setIsEnabled(false);
    }

    // Return release function
    let released = false;
    return () => {
      if (released) return; // Prevent double-release
      released = true;
      disableHoldCount.current -= 1;
      if (disableHoldCount.current === 0) {
        // Last hold released - re-enable universal search
        setIsEnabled(true);
      }
    };
  }, []);

  const value: UniversalSearchContextValue = useMemo(
    () => ({
      isEnabled,
      setEnabled,
      customHandler,
      setCustomHandler,
      acquireDisableHold,
      deviceActionRenderer,
      setDeviceActionRenderer,
      closeSearch,
      setCloseSearch,
    }),
    [
      isEnabled,
      setEnabled,
      customHandler,
      setCustomHandler,
      acquireDisableHold,
      deviceActionRenderer,
      setDeviceActionRenderer,
      closeSearch,
      setCloseSearch,
    ],
  );

  return (
    <UniversalSearchContext.Provider value={value}>{children}</UniversalSearchContext.Provider>
  );
}

export function useUniversalSearchContext() {
  const context = useContext(UniversalSearchContext);
  if (!context) {
    throw new Error("useUniversalSearchContext must be used within a UniversalSearchProvider");
  }
  return context;
}

const defaultSearchContext: UniversalSearchContextValue = {
  isEnabled: false,
  setEnabled: () => {},
  customHandler: null,
  setCustomHandler: () => {},
  acquireDisableHold: () => () => {},
  deviceActionRenderer: null,
  setDeviceActionRenderer: () => {},
  closeSearch: null,
  setCloseSearch: () => {},
};

/**
 * Safe version that returns defaults when used outside UniversalSearchProvider.
 * Use this in components that may or may not be wrapped in the provider.
 */
export function useOptionalUniversalSearchContext(): UniversalSearchContextValue {
  const context = useContext(UniversalSearchContext);
  return context ?? defaultSearchContext;
}
